import React, { useState, useEffect, useContext, useRef } from 'react';
import { useSearchParams } from "react-router-dom";
import { ScrollContext, listTypes, customEventTypes, alfrescoTypes, docTypeSearchParams } from '../../constants.js';
import i18next from "../../i18n.js";
import { Spinner, Form } from 'react-bootstrap';
import { loadListData, renderDocumentItem, subscribeToSignEvents } from "../helpers/HelperFunctions.js";
import { eventDispatcher } from '../../eventDispatcher.js';

const DocumentList = (props) => {
    const [searchParams] = useSearchParams();

    const userData = JSON.parse(localStorage.getItem('user'));
    const showBusinessTrip = userData?.canCreateBusinessTrip;

    const getTypeFilterFromUrl = () => {
        let type = searchParams.get('type');
        switch (type) {
            case docTypeSearchParams.vacationClaim:
                return alfrescoTypes.vacationClaim;
            case docTypeSearchParams.certification:
                return alfrescoTypes.certification;
            case docTypeSearchParams.businessTrip:
                if (showBusinessTrip) {
                    return alfrescoTypes.businessTrip;
                }
                else {
                    return '';
                }
            case docTypeSearchParams.details:
                return alfrescoTypes.details;
            case docTypeSearchParams.other:
                return `${alfrescoTypes.unstructuredDocument}`;
            case docTypeSearchParams.transferRequest:
                return alfrescoTypes.transferRequest;
            default:
                return '';
        }
    }

    const [documents, setDocuments] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(false);
    const [allLoaded, setAllLoaded] = useState(false);
    const [onlyMyDocs, setOnlyMyDocs] = useState(true);
    const [fieldSettings, setFieldSettings] = useState(null);
    const [searchStr, setSearchStr] = useState(null);
    const [typeFilter, setTypeFilter] = useState(getTypeFilterFromUrl());
    const scrollData = useContext(ScrollContext);

    const documentsRef = useRef(null);
    const documentsInSigningRef = useRef([]);

    useEffect(() => {
        documentsRef.current = documents;
    }, [documents])

    useEffect(() => {
        loadItems();

        const searchEventId = eventDispatcher.subscrubeToEvent(customEventTypes.onSearch, onSearch);
        let unsubscribeFunction = null;

        if (props.type === listTypes.Tasks) {
            unsubscribeFunction = subscribeToSignEvents(documentsInSigningRef);
        }

        return () => {
            eventDispatcher.unsubscrubeFromEvent(searchEventId);

            if (unsubscribeFunction) {
                unsubscribeFunction();
            }
        }
    }, []);

    const getFilter = (onlyMyDocs, searchStr, typeFilter) => {
        const filter = onlyMyDocs && props.type !== listTypes.Tasks ? [{
            name: 'Creator',
            values: ['ME']
        }] : [];
        if (searchStr) {
            filter.push({
                name: 'SearchData',
                values: [searchStr]
            })
        }
        if (typeFilter) {
            filter.push({
                name: 'SearchType',
                values: [typeFilter]
            });
        }
        return filter;
    }

    useEffect(() => {
        if (searchStr !== null) {
            setLoading(false);
            setDocuments([]);
            setAllLoaded(false);
            setLoading(true);

            const filter = getFilter(onlyMyDocs, searchStr, typeFilter);
            loadData(0, filter, true);
        }
    }, [searchStr])

    useEffect(() => {
        if (documents.length > 0 && !allLoaded && !loading && scrollData.scrollTop + scrollData.clientHeight + 10 > scrollData.scrollHeight) {
            loadItems();
        }
    }, [scrollData]);

    const loadItems = () => {
        if (!loading && !allLoaded) {
            setLoading(true);

            const filter = getFilter(onlyMyDocs, searchStr, typeFilter);
            loadData(documents.length, filter);
        }
    }

    const loadData = (skip, filter, reset = false) => {
        loadListData(props.type, filter, skip, 0, props.type === listTypes.Documents ? 'CreateDate' : '', true).then(data => {
            if (data && data.items && data.items.length > 0) {
                setNotFound(false);
                if (!fieldSettings) setFieldSettings(data.fieldSettings);

                if (reset) {
                    setDocuments(data.items);
                }
                else {
                    const newDocuments = data.items.filter(it => !documents.some(d => d.id === it.id));
                    setDocuments((prevDocuments) => [...prevDocuments, ...newDocuments]);
                }
            }
            else {
                if (documents.length === 0 || reset) {
                    setNotFound(true);
                }
                else {
                    setNotFound(false);
                }
                setAllLoaded(true);
            }
            setLoading(false);
        });
    }

    const filterTypeChanged = (e) => {
        setTypeFilter(e.target.value);
        setDocuments([]);
        setAllLoaded(false);
        setLoading(true);
        const filter = getFilter(onlyMyDocs, searchStr, e.target.value);
        loadData(0, filter, true);
    }

    const onOnlyMyChanged = (e) => {
        setDocuments([]);
        setAllLoaded(false);
        setOnlyMyDocs(e.target.checked);
        setLoading(true);

        const filter = getFilter(e.target.checked, searchStr, typeFilter);
        loadData(0, filter, true);
    }

    const onSearch = (searchStr) => {
        setSearchStr(searchStr);
    }

    const getNotFoundMessage = () => {
        switch (props.type) {
            case listTypes.Notifications:
                return i18next.t('notifications.noNotificationsLabel');
            case listTypes.Tasks:
                return i18next.t('list.tasksNotFound');
            default:
                return i18next.t('list.documentsNotFound');
        }
    }

    return <div className="doc-list-contaner">
        {props.type === listTypes.Documents && <div className='filter-panel'>
            {<Form.Select value={typeFilter} onChange={filterTypeChanged} className='filter-type-select' id='filter-type-select'>
                <option key="all" value="">{i18next.t('list.typeFilter.all')}</option>
                <option key="claims" value={alfrescoTypes.vacationClaim} >{i18next.t('list.typeFilter.claims')}</option>
                <option key="certifications" value={alfrescoTypes.certification} >{i18next.t('list.typeFilter.certifications')}</option>
                {showBusinessTrip && <option key="businessTrips" value={alfrescoTypes.businessTrip} >{i18next.t('list.typeFilter.businessTrips')}</option>}
                <option key="details" value={alfrescoTypes.details} >{i18next.t('list.typeFilter.details')}</option>
                <option key="transferRequests" value={alfrescoTypes.transferRequest} >{i18next.t('list.typeFilter.transferRequests')}</option>
                {showBusinessTrip && <option key="other" value={`${alfrescoTypes.unstructuredDocument}`} >{i18next.t('list.typeFilter.other')}</option>}
            </Form.Select>}
            <div className='only-my-checkbox'>
                <Form.Check
                    type='checkbox'
                    label={i18next.t('widgets.myDocsCheckBoxLabel')}
                    id='only-my-check-box'
                    checked={onlyMyDocs}
                    onChange={onOnlyMyChanged}
                />
            </div>
        </div>}
        <div className={"doc-list" + (notFound ? ' not-found' : '')}>
            {documents && documents.map((doc) => renderDocumentItem(doc, props.type, fieldSettings))}
            {notFound && <p className="not-found">{getNotFoundMessage()}</p>}
            {loading && <Spinner animation="border" variant="primary" />}
        </div>
    </div>
}

export default DocumentList;